import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Button,
  Space,
  message,
  Alert,
  Statistic,
  Row,
  Col,
  Modal
} from 'antd';
import {
  SafetyOutlined,
  SaveOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import Editor from '../other/Editor';
import {
  getDocumentationPrivacy,
  updatePrivacy,
  clearMessages,
  updatePrivacyDraft,
  resetPrivacyDraft
} from '../../features/account/documentationPrivacySlice';

const PrivacyManager = () => {
  const dispatch = useDispatch();

  const {
    privacy,
    privacyDraft,
    hasUnsavedPrivacy,
    loading,
    privacyLoading,
    error,
    success,
    lastUpdated
  } = useSelector((state) => state.documentationPrivacy);

  useEffect(() => {
    dispatch(getDocumentationPrivacy());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  // Handle privacy content change
  const handlePrivacyChange = (content) => {
    dispatch(updatePrivacyDraft(content));
  };

  // Save privacy
  const handleSavePrivacy = () => {
    dispatch(updatePrivacy(privacyDraft));
  };

  // Reset privacy to saved version
  const handleResetPrivacy = () => {
    Modal.confirm({
      title: 'Reset Privacy Policy',
      content: 'Are you sure you want to discard your changes and reset to the last saved version?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        dispatch(resetPrivacyDraft());
      }
    });
  };

  // Refresh content
  const handleRefresh = () => {
    if (hasUnsavedPrivacy) {
      Modal.confirm({
        title: 'Refresh Content',
        content: 'You have unsaved changes. Refreshing will discard these changes. Continue?',
        icon: <WarningOutlined />,
        okText: 'Refresh',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          dispatch(getDocumentationPrivacy());
        }
      });
    } else {
      dispatch(getDocumentationPrivacy());
    }
  };

  // Get word count
  const getWordCount = (text) => {
    return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  };

  // Get character count
  const getCharCount = (text) => {
    return text.length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
              <SafetyOutlined className="text-xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1">Privacy Policy Manager</h3>
              <p className="text-gray-600">Create and edit your privacy policy</p>
            </div>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            {hasUnsavedPrivacy && (
              <>
                <Button onClick={handleResetPrivacy} disabled={loading}>
                  Reset
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSavePrivacy}
                  loading={privacyLoading}
                >
                  Save Privacy Policy
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* Statistics */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8}>
            <Statistic
              title="Word Count"
              value={getWordCount(privacyDraft)}
              prefix={<SafetyOutlined />}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Characters"
              value={getCharCount(privacyDraft)}
              suffix={`/ 50,000`}
              valueStyle={{ 
                color: getCharCount(privacyDraft) > 45000 ? '#f5222d' : '#52c41a' 
              }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Status"
              value={hasUnsavedPrivacy ? "Unsaved" : "Saved"}
              valueStyle={{ 
                color: hasUnsavedPrivacy ? '#fa8c16' : '#52c41a' 
              }}
            />
          </Col>
        </Row>

        {lastUpdated && (
          <div className="mt-4 text-sm text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </div>
        )}
      </Card>

      {/* Unsaved Changes Alert */}
      {hasUnsavedPrivacy && (
        <Alert
          message="Unsaved Changes"
          description="You have unsaved changes in your privacy policy. Don't forget to save your work."
          type="warning"
          showIcon
          action={
            <Button size="small" type="primary" onClick={handleSavePrivacy}>
              Save Changes
            </Button>
          }
        />
      )}

      {/* Editor */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-lg font-medium">Privacy Policy Content</h4>
              <p className="text-gray-600">
                Define how you collect, use, and protect user data
              </p>
            </div>
          </div>
          
          <div className="editor-container border border-border-light rounded-lg" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
            <Editor
              value={privacyDraft}
              onChange={handlePrivacyChange}
              placeholder="Start writing your privacy policy..."
              minHeight="600px"
              stickyToolbar={true}
              style={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              editorStyle={{
                height: 'calc(100% - 60px)', // Subtract toolbar height
                overflow: 'auto',
                border: 'none'
              }}
              toolbarStyle={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'white',
                borderBottom: '1px solid #e8e8e8',
                borderRadius: '8px 8px 0 0'
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PrivacyManager;